import { Client, Databases, Account, ID, Query } from "appwrite";

// Appwrite configuration
export const appwriteConfig = {
  endpoint: "https://cloud.appwrite.io/v1",
  projectId: "67e12daa0002eec157ab",
  databaseId: "67e2a157000d4c58cc93",
  customerCollectionId: "67f92ae10033c01a8f8c",
  conversationCollectionId: "67e555c10039e259d079",
  messageCollectionId: "67e59d260035707e53e8",
  userCollectionId: "67e535d7003e35a9938d",
};

const client = new Client();
client
  .setEndpoint(appwriteConfig.endpoint)
  .setProject(appwriteConfig.projectId);

const databases = new Databases(client);
const account = new Account(client);

// Cache for user status
let cachedUserStatus = null;
let currentCompanyId = null;
let sessionCache = null;
let companyIdCache = null;

export function setCompanyId(companyId) {
  companyIdCache = companyId;
  console.log("💼 Company ID set for chat:", companyId);
}

// Get user status from Appwrite
// Replace your existing getUserStatus function with this:
export async function getUserStatus(companyId = null) {
  const targetCompanyId = companyId || currentCompanyId || companyIdCache;

  if (!targetCompanyId) {
    console.warn("No company ID provided for status check");
    return {
      status: "online",
      statusText: "Online",
      message: "Wir sind gerade online und bereit, dir zu helfen!",
    };
  }

  try {
    console.log("🔍 Fetching user status for company:", targetCompanyId);

    const response = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.userCollectionId,
      [Query.equal("companyId", targetCompanyId)]
    );

    console.log("📊 Query response:", {
      totalDocuments: response.documents.length,
      documents: response.documents.map(doc => ({
        id: doc.$id,
        companyId: doc.companyId,
        status: doc.status,
        email: doc.email
      }))
    });

    if (response.documents.length === 0) {
      console.warn(`❌ No user found for company ID: ${targetCompanyId}`);
      return {
        status: "offline",
        statusText: "Offline",
        message: "Wir sind momentan offline. Hinterlasse gerne eine Nachricht!",
      };
    }

    const userData = response.documents[0];
    const userStatus = userData.status || "online";

    console.log("✅ Found user data:", {
      userId: userData.$id,
      status: userStatus,
      email: userData.email,
      companyId: userData.companyId
    });

    // Cache the status
    cachedUserStatus = {
      status: userStatus,
      statusText: getStatusText(userStatus),
      message: getStatusMessage(userStatus),
      lastUpdated: new Date().toISOString(),
    };

    console.log("💾 Cached status:", cachedUserStatus);
    return cachedUserStatus;
  } catch (error) {
    console.error("❌ Error fetching user status:", error);
    // Return default online status on error
    return {
      status: "online",
      statusText: "Online",
      message: "Wir sind gerade online und bereit, dir zu helfen!",
    };
  }
}

// Get cached status (for performance)
export function getCachedUserStatus() {
  if (cachedUserStatus && isStatusCacheValid()) {
    return cachedUserStatus;
  }
  return null;
}

// Check if status cache is valid (5 minutes)
function isStatusCacheValid() {
  if (!cachedUserStatus || !cachedUserStatus.lastUpdated) return false;

  const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
  const lastUpdated = new Date(cachedUserStatus.lastUpdated);

  return lastUpdated > fiveMinutesAgo;
}

// Get status text based on status value
function getStatusText(status) {
  switch (status) {
    case "online":
      return "Online";
    case "busy":
      return "Beschäftigt";
    case "away":
      return "Abwesend";
    case "meeting":
      return "Im Meeting";
    case "offline":
      return "Offline";
    default:
      return "Online";
  }
}

// Get status message for tooltip
function getStatusMessage(status) {
  switch (status) {
    case "online":
      return "Wir sind gerade online und bereit, dir zu helfen!";
    case "busy":
      return "Wir sind momentan beschäftigt, antworten aber so schnell wie möglich.";
    case "away":
      return "Wir sind kurz weg, aber deine Nachricht wird bearbeitet.";
    case "meeting":
      return "Wir sind gerade in einem Meeting, melden uns aber bald zurück.";
    case "offline":
      return "Wir sind momentan offline. Hinterlasse gerne eine Nachricht!";
    default:
      return "Wir sind gerade online und bereit, dir zu helfen!";
  }
}

// Get color class based on status for ping indicator
export function getStatusColorClass(status, isPing = false) {
  switch (status) {
    case "online":
      return isPing ? "bg-green-400" : "bg-green-500";
    case "busy":
      return "bg-red-500";
    case "away":
      return "bg-yellow-500";
    case "meeting":
      return "bg-purple-500";
    case "offline":
      return "bg-gray-400";
    default:
      return "bg-green-500";
  }
}

// Get text color based on status
export function getTextColorClass(status) {
  switch (status) {
    case "online":
      return "text-green-600";
    case "busy":
      return "text-red-600";
    case "away":
      return "text-yellow-600";
    case "meeting":
      return "text-purple-600";
    case "offline":
      return "text-gray-600";
    default:
      return "text-green-600";
  }
}

// Update user status (for admin/agent use)
export async function updateUserStatus(newStatus, companyId = null) {
  const targetCompanyId = companyId || currentCompanyId;

  if (!targetCompanyId) {
    throw new Error("No company ID provided for status update");
  }

  try {
    console.log("Updating user status to:", newStatus);

    const response = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.userCollectionId,
      [Query.equal("companyId", targetCompanyId)]
    );

    if (response.documents.length === 0) {
      throw new Error(`No user found for company ID: ${targetCompanyId}`);
    }

    const userDoc = response.documents[0];

    const updatedUser = await databases.updateDocument(
      appwriteConfig.databaseId,
      appwriteConfig.userCollectionId,
      userDoc.$id,
      {
        status: newStatus,
        updated_at: new Date().toISOString(),
      }
    );

    // Update cache
    cachedUserStatus = {
      status: newStatus,
      statusText: getStatusText(newStatus),
      message: getStatusMessage(newStatus),
      lastUpdated: new Date().toISOString(),
    };

    console.log("User status updated successfully");
    return updatedUser;
  } catch (error) {
    console.error("Error updating user status:", error);
    throw error;
  }
}

// Set up real-time status monitoring (optional)
// Replace your existing startStatusMonitoring function with this:
export function startStatusMonitoring(companyId = null, onStatusChange = null) {
  const targetCompanyId = companyId || currentCompanyId || companyIdCache;

  if (!targetCompanyId) {
    console.warn("❌ No company ID for status monitoring");
    return () => {}; // Return empty cleanup function
  }

  console.log("🔄 Starting status monitoring for company:", targetCompanyId);

  // Store the current status for comparison
  let lastKnownStatus = cachedUserStatus?.status || null;

  // Poll for status changes every 30 seconds
  const statusInterval = setInterval(async () => {
    try {
      console.log("🔄 Checking for status updates...");
      const currentStatus = await getUserStatus(targetCompanyId);

      // If we have a callback and status changed, notify
      if (onStatusChange && lastKnownStatus !== null) {
        if (currentStatus.status !== lastKnownStatus) {
          console.log(`📢 Status change detected: ${lastKnownStatus} → ${currentStatus.status}`);
          onStatusChange(currentStatus, lastKnownStatus);
        }
      }

      // Update the last known status
      lastKnownStatus = currentStatus.status;
      
    } catch (error) {
      console.error("❌ Error monitoring status:", error);
    }
  }, 30000); // Check every 30 seconds

  return () => {
    console.log("🛑 Stopping status monitoring");
    clearInterval(statusInterval);
  }; // Return cleanup function
}

// Get user's IP and location info using ipapi
const getUserLocationInfo = async () => {
  try {
    console.log("🌍 Fetching user location...");
    const response = await fetch("https://ipapi.co/json/");
    const data = await response.json();

    return {
      ip: data.ip || "0.0.0.0",
      country: data.country_code || "CH",
      countryName: data.country_name || "Switzerland",
      city: data.city || "Unknown",
      language: data.languages?.split(",")[0] || "de",
    };
  } catch (error) {
    console.error("❌ Error fetching location:", error);
    return {
      ip: "0.0.0.0",
      country: "CH",
      countryName: "Switzerland",
      city: "Unknown",
      language: "de",
    };
  }
};

// Get browser information
const getBrowserInfo = () => {
  const userAgent = navigator.userAgent;

  if (userAgent.match(/chrome|chromium|crios/i)) return "Chrome";
  if (userAgent.match(/firefox|fxios/i)) return "Firefox";
  if (userAgent.match(/safari/i)) return "Safari";
  if (userAgent.match(/opr\//i)) return "Opera";
  if (userAgent.match(/edg/i)) return "Edge";
  return "Unknown";
};

// Create or get anonymous session
// Fixed getOrCreateAnonymousSession function
const getOrCreateAnonymousSession = async () => {
  try {
    // ✅ FIXED: First check if we already have an active session
    try {
      const currentSession = await account.getSession('current');
      if (currentSession) {
        console.log("✅ Using existing session:", currentSession.$id);
        sessionCache = currentSession;
        return currentSession;
      }
    } catch (sessionError) {
      // No active session found, continue to create one
      console.log("ℹ️ No active session found, creating new one");
    }

    // Use cached session if available
    if (sessionCache) {
      console.log("✅ Using cached session");
      return sessionCache;
    }

    // Try to create anonymous session only if no active session exists
    const session = await account.createAnonymousSession();
    sessionCache = session;
    console.log("✅ Created new anonymous session:", session.$id);
    return session;
  } catch (error) {
    console.error("❌ Error with session:", error);
    
    // ✅ FIXED: If session creation fails due to existing session, try to get it
    if (error.message.includes('session is active')) {
      try {
        const existingSession = await account.getSession('current');
        sessionCache = existingSession;
        console.log("✅ Retrieved existing active session:", existingSession.$id);
        return existingSession;
      } catch (getError) {
        console.error("❌ Could not retrieve existing session:", getError);
      }
    }
    
    throw new Error(`Session handling failed: ${error.message}`);
  }
};

// Check if customer already exists
const findExistingCustomer = async (email, companyId) => {
  try {
    console.log(
      `🔍 Checking for existing customer: ${email} in company: ${companyId}`
    );

    const customers = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.customerCollectionId,
      [Query.equal("email", email), Query.equal("companyId", companyId)]
    );

    if (customers.documents.length > 0) {
      console.log(
        "✅ Found existing customer:",
        customers.documents[0].username
      );
      return customers.documents[0];
    }

    console.log("ℹ️ No existing customer found");
    return null;
  } catch (error) {
    console.error("❌ Error finding customer:", error);
    return null;
  }
};

// Create new customer
const createCustomer = async (formData, companyId) => {
  try {
    console.log("👤 Creating new customer...");

    // Get location and browser info
    const locationInfo = await getUserLocationInfo();
    const browser = getBrowserInfo();
    const anonymousId = ID.unique();
const now = new Date().toISOString();
    const customerData = {
      companyId: companyId,
      email: formData.email.trim(),
      username: formData.name.trim(),
      first_name: formData.name.split(" ")[0] || formData.name.trim(),
      last_name: formData.name.split(" ").slice(1).join(" ") || "",
      avatar: null,
      ip: locationInfo.ip,
      countryName: locationInfo.countryName,
      country: locationInfo.country,
      city: locationInfo.city,
      language: locationInfo.language,
      browser: browser,
      status: "active",
      is_anonymous: true,
      anonymousId: anonymousId,
created_at: now,
updated_at: now,
      notes: [],
    };

    console.log("📝 Customer data prepared:", {
      ...customerData,
      ip: "[hidden]",
    });

    const customer = await databases.createDocument(
      appwriteConfig.databaseId,
      appwriteConfig.customerCollectionId,
      ID.unique(),
      customerData
    );

    console.log("✅ Customer created successfully:", customer.username);
    return customer;
  } catch (error) {
    console.error("❌ Error creating customer:", error);
    throw new Error(`Customer creation failed: ${error.message}`);
  }
};

// Create new conversation
const createConversation = async (customer, companyId) => {
  try {
    console.log("💬 Creating new conversation...");
const now = new Date().toISOString();
    const conversationData = {
      title: `Chat mit ${customer.username}`,
      participants: [customer.$id], // Array with customer ID
      created_at: now,
      updated_at: now,
      last_message_preview: `Chat started by ${customer.username}`,
      companyId: companyId,
      channel: "messenger",
      status: "inbox",
      unreadCount: 0,
      country: customer.country || "CH",
      phone_number: null,
      messageStatus: "sent",
      rating: null,
      topic: "general",
      is_read: false,
      isNewConversation: true,
      lastReadBy: [],
      last_sender_id: customer.$id,
    };

    console.log("📝 Conversation data prepared");

    const conversation = await databases.createDocument(
      appwriteConfig.databaseId,
      appwriteConfig.conversationCollectionId,
      ID.unique(),
      conversationData
    );

    console.log("✅ Conversation created successfully:", conversation.$id);
    return conversation;
  } catch (error) {
    console.error("❌ Error creating conversation:", error);
    throw new Error(`Conversation creation failed: ${error.message}`);
  }
};

// Main function: Handle customer onboarding
export const handleCustomerOnboarding = async (formData) => {
  try {
    console.log("🚀 Starting customer onboarding...", formData);

    // Validate required data
    if (!formData.name?.trim() || !formData.email?.trim()) {
      throw new Error("Name and email are required");
    }

    if (!companyIdCache) {
      throw new Error(
        "Company ID not set. Please initialize with setCompanyId()"
      );
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      throw new Error("Please enter a valid email address");
    }

    // Step 1: Create or get anonymous session
    const session = await getOrCreateAnonymousSession();

    // Step 2: Check if customer already exists
    let customer = await findExistingCustomer(formData.email, companyIdCache);

    // Step 3: Create customer if doesn't exist
    if (!customer) {
      customer = await createCustomer(formData, companyIdCache);
    } else {
      // Update existing customer's info if needed
      const updateData = {
        username: formData.name.trim(),
        updated_at: new Date().toISOString(),
      };

      customer = await databases.updateDocument(
        appwriteConfig.databaseId,
        appwriteConfig.customerCollectionId,
        customer.$id,
        updateData
      );
    }

    // Step 4: Create new conversation
    const conversation = await createConversation(customer, companyIdCache);

    console.log("🎉 Customer onboarding completed successfully!");

    return {
      success: true,
      customer: customer,
      conversation: conversation,
      session: session,
      message: "Customer onboarding completed successfully",
    };
  } catch (error) {
    console.error("❌ Customer onboarding failed:", error);

    return {
      success: false,
      error: error.message,
      customer: null,
      conversation: null,
    };
  }
};

// Check if there's an existing session
export const checkExistingSession = async () => {
  try {
    // Try to get current session from cache first
    if (sessionCache) {
      return {
        hasSession: true,
        session: sessionCache,
      };
    }

    // Try to get current session from Appwrite
    const session = await client.account.getSession("current");
    sessionCache = session;

    return {
      hasSession: true,
      session: session,
    };
  } catch (error) {
    console.log("ℹ️ No existing session found");
    return {
      hasSession: false,
      session: null,
    };
  }
};

// Clear session and cache
export const clearSession = async () => {
  try {
    await client.account.deleteSession("current");
    sessionCache = null;
    console.log("✅ Session cleared successfully");
    return true;
  } catch (error) {
    console.error("❌ Error clearing session:", error);
    return false;
  }
};

// Get customer by ID
export const getCustomerById = async (customerId) => {
  try {
    const customer = await databases.getDocument(
      appwriteConfig.databaseId,
      appwriteConfig.customerCollectionId,
      customerId
    );
    return customer;
  } catch (error) {
    console.error("❌ Error fetching customer:", error);
    return null;
  }
};

// Update customer information
export const updateCustomer = async (customerId, updateData) => {
  try {
    const updatedCustomer = await databases.updateDocument(
      appwriteConfig.databaseId,
      appwriteConfig.customerCollectionId,
      customerId,
      {
        ...updateData,
        updated_at: new Date().toISOString(),
      }
    );

    console.log("✅ Customer updated successfully");
    return updatedCustomer;
  } catch (error) {
    console.error("❌ Error updating customer:", error);
    throw error;
  }
};

export default {
  setCompanyId,
  getUserStatus,
  getCachedUserStatus,
  getStatusColorClass,
  getTextColorClass,
  updateUserStatus,
  startStatusMonitoring,
  handleCustomerOnboarding,
  checkExistingSession,
  clearSession,
  getCustomerById,
  updateCustomer,
};
